import { QueryError } from '@graphql-box/helpers';

import { useMultiQuery } from '@graphql-box/react';
import { type Company } from '@tmdb-graphql-api/schema/types';
import { useEffect, useState } from 'react';
import { type SetRequired } from 'type-fest';
import { encodeId } from '#helpers/encodeId.ts';
import { InnerQuery } from './InnerQuery.tsx';
import { mergePopulatedValue } from './helpers/mergePopulatedValue.ts';
import { type CompanyOption, type OuterQueryProps } from './types.ts';

const GET_COMPANY_DETAILS = "query GetCompanyDetails(\n  $id: ID!\n) {\n  company(id: $id) {\n    description\n    headquarters\n    homepage\n    logoPath\n    name\n  }\n}\n";

export const OuterQuery = ({ ariaLabelledBy, name, setFieldValue, value }: OuterQueryProps) => {
  const { execute, results = [] } = useMultiQuery<{ company: Company }>(GET_COMPANY_DETAILS, {
    loading: true,
  });

  const [populatedValue, setPopulatedValue] = useState<SetRequired<CompanyOption, 'name'>[]>([]);
  const valueKey = encodeId(value);

  useEffect(() => {
    const options = value.filter(entry => !entry.name).map(entry => ({ variables: { id: entry.id } }));
    void execute(options, { data: { initiator: 'CompanyMultiSelectDropdown' } });
    // We only want to re-execute when valueKey changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueKey]);

  const data: Company[] = [];
  const errors: QueryError[] = [];

  for (const result of results) {
    if (result instanceof QueryError) {
      errors.push(result);
    } else {
      data.push(result.data.company);
    }
  }

  if (errors.length > 0) {
    console.log(errors);
  }

  useEffect(() => {
    if (data.length > 0) {
      const newPopulatedValue = data.map(entry => ({
        id: entry.id,
        logoPath: entry.logoPath,
        name: entry.name,
      }));

      setPopulatedValue(newPopulatedValue);
    }

    // We only want to re-execute when valueKey and data changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueKey, data.length]);

  const mergedValue = mergePopulatedValue(populatedValue, value);
  return <InnerQuery ariaLabelledBy={ariaLabelledBy} name={name} setFieldValue={setFieldValue} value={mergedValue} />;
};
